<template>
  <div class="signup-entry signup-atmosphere">
    <CloudLayer />

    <header class="signup-entry__topbar signup-shell-content">
      <BrandMark />
    </header>

    <main class="signup-entry__main signup-shell-content">
      <section class="signup-entry__card signup-card signup-fade-up">
        <div class="signup-entry__header signup-stagger">
          <div class="signup-eyebrow">Happily for teams</div>
          <h1 class="signup-display signup-display--sm">
            Let's get your team
            <b>started</b>.
          </h1>
          <p class="signup-subtitle">
            Create your free Happily account in a few minutes. We'll send a one-time code to your work email.
          </p>
        </div>

        <form class="signup-entry__form" novalidate @submit.prevent="onSubmit">
          <label class="signup-field">
            <span class="signup-field__label">Your name</span>
            <input
              ref="nameInput"
              v-model="signerName"
              type="text"
              name="name"
              autocomplete="name"
              required
              :disabled="submitting"
              placeholder="Jane Doe"
              class="signup-field__input"
            />
          </label>

          <label class="signup-field">
            <span class="signup-field__label">Work email</span>
            <input
              ref="emailInput"
              v-model="email"
              type="email"
              name="email"
              autocomplete="email"
              required
              :disabled="submitting"
              placeholder="you@company.com"
              :class="['signup-field__input', { 'signup-field__input--error': !!emailGateError }]"
              @focus="onEmailFocus"
              @blur="onEmailBlur"
            />
            <span v-if="emailGateError" class="signup-field__error" role="alert">
              {{ emailGateError }}
            </span>
          </label>

          <label class="signup-field">
            <span class="signup-field__label">Company name</span>
            <input
              v-model="companyName"
              type="text"
              name="organization"
              autocomplete="organization"
              required
              :disabled="submitting"
              placeholder="Acme Co."
              class="signup-field__input"
            />
          </label>

          <p class="signup-entry__terms">
            By continuing, you agree to our
            <a href="https://happily.ai/terms-of-service" target="_blank" rel="noopener noreferrer">Terms</a>
            and
            <a href="https://happily.ai/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </p>

          <button
            type="submit"
            class="signup-btn signup-btn--primary signup-btn--block"
            :disabled="!canSubmit"
          >
            <span v-if="submitting" class="signup-entry__spinner" aria-hidden="true"></span>
            <span>{{ submitting ? 'Sending code…' : 'Send verification code' }}</span>
          </button>

          <p v-if="errorMessage" class="signup-error" role="alert">
            {{ errorMessage }}
          </p>
        </form>

        <p class="signup-foot-note">
          Already have an account?
          <router-link to="/login">Log in</router-link>
        </p>
      </section>
    </main>
  </div>
</template>

<script>
import BrandMark from './BrandMark.vue'
import CloudLayer from './CloudLayer.vue'
import { classifyEmail, emailGateMessage } from './_email-domains'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'SignupEntry',
  components: { BrandMark, CloudLayer },
  data() {
    return {
      signerName: '',
      email: '',
      companyName: '',
      // Show the work-email error only when the field has been blurred
      // AND is not currently focused. Re-focusing (to type a fix) hides
      // the error again until the next blur.
      emailTouched: false,
      submitting: false,
      errorMessage: '',
    }
  },
  computed: {
    isEmailWellFormed() {
      return EMAIL_RE.test(String(this.email || '').trim())
    },
    emailClassification() {
      return classifyEmail(this.email)
    },
    isWorkEmail() {
      return this.emailClassification === 'ok'
    },
    /** Inline error under the email field — only after blur. */
    emailGateError() {
      if (!this.emailTouched) return ''
      if (!this.email) return ''
      if (!this.isEmailWellFormed) return emailGateMessage('invalid')
      if (this.isWorkEmail) return ''
      return emailGateMessage(this.emailClassification)
    },
    isNameValid() {
      return String(this.signerName || '').trim().length > 0
    },
    canSubmit() {
      return (
        !this.submitting &&
        this.isNameValid &&
        this.isWorkEmail &&
        String(this.companyName || '').trim().length > 0
      )
    },
  },
  mounted() {
    // Prefill from persisted state if the user bounced back to /signup.
    const s = this.$store.state.signup
    if (s) {
      if (s.signerName && !this.signerName) this.signerName = s.signerName
      if (s.email && !this.email) this.email = s.email
      if (s.wizardData && s.wizardData.company && s.wizardData.company.name) {
        this.companyName = s.wizardData.company.name
      }
    }
    this.$nextTick(() => {
      // Focus whichever field the user hasn't filled yet — name first,
      // then email, then nothing if everything is hydrated.
      if (this.$refs.nameInput && !this.signerName) {
        this.$refs.nameInput.focus()
      } else if (this.$refs.emailInput && !this.email) {
        this.$refs.emailInput.focus()
      }
    })
  },
  methods: {
    onEmailFocus() {
      // Hide the error while the user is typing the fix.
      this.emailTouched = false
    },
    onEmailBlur() {
      this.emailTouched = true
    },
    async onSubmit() {
      // Mark email as touched so the inline error surfaces if the user
      // hit Enter without ever blurring the field.
      this.emailTouched = true
      if (!this.canSubmit) return
      this.errorMessage = ''
      this.submitting = true
      try {
       const result = await this.$store.dispatch('signup/verifyEmail', { email: this.email })
        if (!result) {
          this.errorMessage = 'Email already exists'
          return
        }
        const signerName = String(this.signerName).trim()
        const email = String(this.email).trim()
        const companyName = String(this.companyName).trim()
        await this.$store.dispatch('signup/requestPin', { email, companyName, signerName })
        // Seed the wizard's company.name with what the user just typed so
        // the wizard's Step 1 is pre-populated and the verify page has
        // the company name on hand for a resend.
        this.$store.commit('signup/UPDATE_COMPANY', { name: companyName })
        this.$router.push('/signup/verify')
      } catch (err) {
        const message =
          (err && err.message) ||
          (err && err.response && err.response.data && err.response.data.message) ||
          "We couldn't send the code. Please try again."
        this.errorMessage = message
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './_shared.scss';

.signup-entry {
  display: flex;
  flex-direction: column;
}

/* Pre-wizard pages give the brand mark the spotlight: centered above the
   card with no competing chrome. Once the wizard kicks in (progress
   indicator), the brand cedes to the top-left corner — see SignupWizard. */
.signup-entry__topbar {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 32px;

  @media (max-width: 640px) { padding: 16px 20px; }
}

.signup-entry__main {
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 24px 24px 64px;

  @media (max-width: 640px) { padding: 12px 16px 40px; }
}

.signup-entry__card {
  width: 100%;
  max-width: 480px;
  padding: 40px 36px 32px;

  @media (max-width: 640px) {
    padding: 28px 22px 24px;
    border-radius: 14px;
  }
}

.signup-entry__header {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 28px;
}

.signup-entry__form {
  display: block;
}

.signup-entry__terms {
  font-size: 12px;
  color: var(--signup-text-muted);
  line-height: 1.5;
  margin: -2px 0 20px;

  a {
    color: var(--signup-brand);
    font-weight: 700;
    text-decoration: none;

    &:hover { text-decoration: underline; }
    &:focus-visible {
      outline: 2px solid var(--signup-gold);
      outline-offset: 2px;
      border-radius: 4px;
    }
  }
}

.signup-entry__spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #ffffff;
  animation: entry-spin 720ms linear infinite;
  margin-right: 2px;
}
@keyframes entry-spin {
  to { transform: rotate(360deg); }
}
</style>
